<template>
  <div class="ad-page">
    <div class="ad-toolbar">
      <div class="ad-search">
        <span class="mdi mdi-magnify"></span>
        <input v-model="search" class="ad-input" placeholder="Buscar por nombre, correo o parque…" aria-label="Buscar usuarios" />
      </div>
      <button type="button" class="chip" :class="{ 'chip--on': filter === 'pending' }" @click="toggleFilter('pending')">Sin activar</button>
      <button type="button" class="chip" :class="{ 'chip--on': filter === 'suspended' }" @click="toggleFilter('suspended')">Suspendidos</button>
    </div>

    <div class="ad-card">
      <p v-if="loading && !users.length" class="ad-empty">Cargando usuarios…</p>
      <p v-else-if="loadError" class="ad-empty">{{ loadError }}</p>
      <p v-else-if="!users.length" class="ad-empty">No hay usuarios que coincidan.</p>
      <div v-else class="ad-table-wrap">
        <table class="ad-table">
          <thead>
            <tr><th>Usuario</th><th>Correo</th><th>Parque</th><th>Rol</th><th>Último acceso</th><th class="ad-col-actions"></th></tr>
          </thead>
          <tbody>
            <tr v-for="u in users" :key="u.key">
              <td>
                <div class="ad-cell-name">
                  {{ u.name || '—' }}
                  <span v-if="u.status === 'PENDING'" class="ad-pill" :class="u.invitationExpired ? 'ad-pill--amber' : 'ad-pill--gray'" style="margin-left: 6px">
                    {{ u.invitationExpired ? 'Invitación vencida' : 'Sin activar' }}
                  </span>
                  <span v-else-if="u.status === 'SUSPENDED'" class="ad-pill ad-pill--red" style="margin-left: 6px">Suspendido</span>
                </div>
              </td>
              <td>{{ u.email }}</td>
              <td><span class="ad-tag">{{ u.parkName }}</span></td>
              <td><span class="ad-pill" :class="ROLE_PILL[u.role] || 'ad-pill--gray'">{{ u.roleLabel }}</span></td>
              <td>{{ relativeTime(u.lastLoginAt) }}</td>
              <td class="ad-col-actions"><RowMenu :items="menuFor(u)" @select="(key) => onAction(key, u)" /></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="users.length >= 300" class="ad-hint" style="padding: 8px 14px">Se muestran los 300 más recientes: usa el buscador para afinar.</p>
    </div>

    <ReasonModal
      v-if="modal?.type === 'suspend'"
      :title="`Suspender cuenta · ${modal.user.name || modal.user.email}`"
      message="Se cerrarán sus sesiones abiertas y no podrá entrar hasta que reactives la cuenta."
      confirm-label="Suspender cuenta"
      :action="(reason) => suspendUser(modal.user.kind, modal.user.id, reason)"
      @close="modal = null"
      @done="afterAction('Cuenta suspendida')"
    />
    <InviteLinkModal
      v-if="modal?.type === 'invite'"
      title="Link de activación"
      :intro="`Comparte este link con ${modal.user.name || modal.user.email} para que cree su contraseña. El anterior deja de funcionar.`"
      :url="modal.inviteUrl"
      :expires-at="modal.expiresAt"
      @close="modal = null"
    />
    <EditUserModal v-if="modal?.type === 'edit'" :user="modal.user" @close="modal = null" @done="onEdited" />
 <ResetPasswordModal v-if="modal?.type === 'reset'" :user="modal.user" @close="modal = null" @done="load" />
    <UserLoginsModal v-if="modal?.type === 'logins'" :user="modal.user" @close="modal = null" />
    <div v-if="toast" class="ad-toast" role="status">{{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { listUsers, regenerateInvitation, closeUserSessions, reactivateUser, suspendUser } from '@/services/superadmin'
import RowMenu from '@/components/Superadmin/RowMenu.vue'
import ReasonModal from '@/components/Superadmin/ReasonModal.vue'
import InviteLinkModal from '@/components/Superadmin/InviteLinkModal.vue'
import ResetPasswordModal from '@/components/Superadmin/ResetPasswordModal.vue'
import EditUserModal from '@/components/Superadmin/EditUserModal.vue'
import UserLoginsModal from '@/components/Superadmin/UserLoginsModal.vue'
import { ROLE_PILL, relativeTime, errorText } from '@/components/Superadmin/adminFormat'

const users = ref([])
const loading = ref(true)
const loadError = ref('')
const search = ref('')
const filter = ref('')
const modal = ref(null)
const toast = ref('')

let seq = 0
async function load() {
  const mine = ++seq
  loading.value = true
  loadError.value = ''
  try {
    const rows = await listUsers({ search: search.value.trim(), filter: filter.value })
    if (mine === seq) users.value = rows
  } catch (err) {
    if (mine === seq) loadError.value = errorText(err, 'No se pudieron cargar los usuarios.')
  } finally {
    if (mine === seq) loading.value = false
  }
}

let timer = null
watch(search, () => { clearTimeout(timer); timer = setTimeout(load, 250) })
onMounted(load)

function toggleFilter(name) {
  filter.value = filter.value === name ? '' : name
  load()
}

function menuFor(u) {
  const items = []
  if (u.status === 'PENDING') {
    items.push({ key: 'invite', label: u.invitationExpired ? 'Regenerar link de activación' : 'Reenviar invitación de activación', icon: 'mdi-link-variant' })
  }
  if (u.status === 'ACTIVE') items.push({ key: 'reset', label: 'Restablecer contraseña', icon: 'mdi-lock-reset' })
  if (u.kind === 'staff') items.push({ key: 'edit', label: 'Editar nombre y correo', icon: 'mdi-pencil-outline' })
  if (u.status !== 'SUSPENDED') items.push({ key: 'logout', label: 'Cerrar sesión en todos los dispositivos', icon: 'mdi-logout-variant', disabled: u.status === 'PENDING' && !u.lastLoginAt })
  items.push(u.status === 'SUSPENDED'
    ? { key: 'reactivate', label: 'Reactivar cuenta', icon: 'mdi-play-circle-outline' }
    : { key: 'suspend', label: 'Suspender cuenta', icon: 'mdi-pause-circle-outline', danger: true })
  items.push({ divider: true }, { key: 'logins', label: 'Ver últimos 10 accesos', icon: 'mdi-history' })
  return items
}

function flash(text) {
  toast.value = text
  setTimeout(() => { toast.value = '' }, 2600)
}

function afterAction(text) {
  modal.value = null
  flash(text)
  load()
}

function onEdited(res) {
  const user = modal.value?.user
  if (res?.inviteUrl) {
    modal.value = { type: 'invite', user: { ...user, email: user.email }, inviteUrl: res.inviteUrl, expiresAt: res.expiresAt }
    flash('Cambios guardados')
    load()
  } else {
    afterAction('Cambios guardados')
  }
}

async function onAction(key, u) {
  try {
    if (key === 'suspend') modal.value = { type: 'suspend', user: u }
    else if (key === 'edit') modal.value = { type: 'edit', user: u }
    else if (key === 'reset') modal.value = { type: 'reset', user: u }
    else if (key === 'logins') modal.value = { type: 'logins', user: u }
    else if (key === 'invite') {
      const { inviteUrl, expiresAt } = await regenerateInvitation(u.kind, u.id)
      modal.value = { type: 'invite', user: u, inviteUrl, expiresAt }
      load()
    } else if (key === 'logout') {
      await closeUserSessions(u.kind, u.id)
      flash('Sesiones cerradas')
    } else if (key === 'reactivate') {
      await reactivateUser(u.kind, u.id)
      afterAction('Cuenta reactivada')
    }
  } catch (err) {
    flash(errorText(err))
  }
}
</script>

<style scoped>
.chip {
  border: 1px solid #d5dbe6; background: #fff; color: #475569; border-radius: 999px; padding: 6px 14px; cursor: pointer;
  font: 500 12px Inter, sans-serif;
}
.chip:hover { background: #f1f5f9; }
.chip--on { background: #1e3a5f; color: #fff; border-color: #1e3a5f; }
</style>